import React, { useState, useRef, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import { Modal, Button, Form, Alert, Spinner, Row, Col } from "react-bootstrap";
import html2canvas from "html2canvas";
import { createSupportRequest, askSupport } from "../../atasksapi";
import ImageAnnotator from "../Sessions/EditImages";

export function SupportModal({ show, onHide }) {
  // Chat view state
  const [view, setView] = useState("chat"); // "chat" | "form"
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isAsking, setIsAsking] = useState(false);
  const [askError, setAskError] = useState("");
  const threadRef = useRef(null);

  // Form state (preserved from original implementation)
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("Medium");
  const [screenshot, setScreenshot] = useState(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const fileInputRef = useRef(null);
  const annotatorRef = useRef(null);

  const pageUrl = window.location.href;

  // Auto-scroll thread to bottom when messages or loading state changes
  useEffect(() => {
    if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
  }, [messages, isAsking]);

  const resetForm = () => {
    setDescription("");
    setPriority("Medium");
    setScreenshot(null);
    setSuccessMsg("");
    setErrorMsg("");
  };

  const handleClose = () => {
    setView("chat");
    setMessages([]);
    setInput("");
    setAskError("");
    resetForm();
    onHide();
  };

  const handleAsk = async () => {
    const trimmed = input.trim();
    if (!trimmed || isAsking) return;

    const userMsg = { role: "user", content: trimmed };
    const currentMessages = [...messages, userMsg];
    setMessages(currentMessages);
    setInput("");
    setIsAsking(true);
    setAskError("");

    // Build history from messages before this new user message
    const history = messages.map(({ role, content }) => ({ role, content }));

    try {
      const data = await askSupport({ question: trimmed, history, pageUrl: window.location.href });
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: data.answer, sourceTitle: data.sourceTitle || null },
      ]);
    } catch (err) {
      setAskError("Failed to get a response. Please try again.");
    } finally {
      setIsAsking(false);
    }
  };

  const handleCaptureScreenshot = async () => {
    setIsCapturing(true);
    const modalBackdrops = document.querySelectorAll(".modal-backdrop, .modal");
    modalBackdrops.forEach((el) => (el.style.visibility = "hidden"));

    try {
      await new Promise((r) => setTimeout(r, 200));
      const canvas = await html2canvas(document.body, {
        useCORS: true,
        scale: 1,
        logging: false,
      });
      setScreenshot(canvas.toDataURL("image/png"));
    } catch (err) {
      console.error("Screenshot capture failed:", err);
      setErrorMsg("Could not capture screenshot. Try uploading one instead.");
    } finally {
      modalBackdrops.forEach((el) => (el.style.visibility = ""));
      setIsCapturing(false);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setScreenshot(ev.target.result);
    reader.readAsDataURL(file);
  };

  const dataURLtoBlob = (dataUrl) => {
    const [header, base64] = dataUrl.split(",");
    const mime = header.match(/:(.*?);/)[1];
    const binary = atob(base64);
    const array = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) array[i] = binary.charCodeAt(i);
    return new Blob([array], { type: mime });
  };

  const handleSubmit = async () => {
    if (!description.trim()) {
      setErrorMsg("Please enter a description of the issue.");
      return;
    }

    setIsSubmitting(true);
    setErrorMsg("");

    try {
      let screenshotBlob = null;
      if (screenshot) {
        let finalScreenshot = screenshot;
        if (annotatorRef.current) {
          try {
            const merged = await annotatorRef.current.mergeImage();
            if (merged) finalScreenshot = merged;
          } catch {
            // fall back to original screenshot
          }
        }
        screenshotBlob = dataURLtoBlob(finalScreenshot);
      }

      await createSupportRequest({
        description: description.trim(),
        priority,
        pageUrl,
        screenshot: screenshotBlob,
      });

      setSuccessMsg("Support request submitted successfully!");
      setTimeout(() => {
        handleClose();
      }, 1500);
    } catch (err) {
      console.error("Failed to submit support request:", err);
      setErrorMsg("Failed to submit. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      {view === "chat" ? (
        <>
          <Modal.Header closeButton>
            <Modal.Title className="text-primary">
              <i className="fa-light fa-life-ring me-2" />
              Support
            </Modal.Title>
          </Modal.Header>

          <Modal.Body>
            <div
              className="d-flex flex-column gap-2"
              style={{ minHeight: 300, maxHeight: 400, overflowY: "auto" }}
              ref={threadRef}
            >
              {messages.length === 0 && (
                <p className="text-muted text-center mt-4 small">
                  Ask a question about PractoSync to get started.
                </p>
              )}
              {messages.map((msg, i) => (
                <div
                  key={i}
                  className={`d-flex ${msg.role === "user" ? "justify-content-end" : "justify-content-start"}`}
                >
                  <div
                    className={`p-2 rounded ${msg.role === "user" ? "bg-primary text-white" : "bg-light border"}`}
                    style={{ maxWidth: "80%", fontSize: "0.9rem" }}
                  >
                    {msg.role === "assistant" ? (
                      <ReactMarkdown>{msg.content}</ReactMarkdown>
                    ) : (
                      <div>{msg.content}</div>
                    )}
                    {msg.role === "assistant" && msg.sourceTitle && (
                      <div className="text-muted mt-1" style={{ fontSize: "0.75rem" }}>
                        Source: {msg.sourceTitle}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {isAsking && (
                <div className="d-flex justify-content-start">
                  <div className="p-2 rounded bg-light border" style={{ fontSize: "0.9rem" }}>
                    <Spinner animation="border" size="sm" className="me-1" />
                    Thinking...
                  </div>
                </div>
              )}
            </div>
            {askError && (
              <Alert variant="danger" className="mt-2 mb-0 py-1 small">
                {askError}
              </Alert>
            )}
          </Modal.Body>

          <Modal.Footer>
            <div className="w-100 d-flex flex-column gap-2">
              <div className="d-flex gap-2">
                <Form.Control
                  type="text"
                  placeholder="Ask a question..."
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleAsk();
                    }
                  }}
                  disabled={isAsking}
                  style={{ height: "38px" }}
                />
                <Button
                  variant="primary"
                  onClick={handleAsk}
                  style={{ height: "38px", width: "38px", padding: 0 }}
                  disabled={isAsking || !input.trim()}
                >
                  <i className="fa-light fa-paper-plane" />
                </Button>
              </div>
              <div className="text-center">
                <Button variant="primary" onClick={() => setView("form")}>
                 <i className="fa-light fa-truck-medical me-2"></i> Contact Support
                </Button>
              </div>
            </div>
          </Modal.Footer>
        </>
      ) : (
        <>
          <Modal.Header closeButton>
            <Modal.Title className="text-primary">
              <i className="fa-light fa-life-ring me-2" />
              Support Request
            </Modal.Title>
          </Modal.Header>

          <Modal.Body>
            {successMsg && <Alert variant="success">{successMsg}</Alert>}
            {errorMsg && (
              <Alert variant="danger" dismissible onClose={() => setErrorMsg("")}>
                {errorMsg}
              </Alert>
            )}

            <Form>
              {/* Description + Priority side by side */}
              <Row className="mb-2">
                <Col xs={9} className="d-flex flex-column justify-content-end">
                  <Form.Label className="fw-semibold">
                    Describe the issue <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    placeholder="Please describe what happened, what you expected, and any steps to reproduce..."
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    disabled={isSubmitting}
                    style={{ flex: 1, resize: "none" }}
                  />
                </Col>
                <Col xs={3} className={"d-flex flex-column gap-2 justify-content-end" + (isSubmitting ? " disabled" : "")}>
                  <Form.Label className="fw-semibold">Priority</Form.Label>
                  <div className="d-flex flex-column gap-2">
                    {["High", "Medium", "Low"].map((p) => (
                      <Form.Check
                        key={p}
                        type="radio"
                        id={`priority-${p}`}
                        name="priority"
                        label={
                          <span
                            
                          >
                            {p}
                          </span>
                        }
                        checked={priority === p}
                        onChange={() => setPriority(p)}
                        disabled={isSubmitting}
                      />
                    ))}
                  </div>
                </Col>
              </Row>

              {/* Screenshot */}
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Screenshot</Form.Label>
                <div className="d-flex gap-2 mb-2">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={handleCaptureScreenshot}
                    disabled={isCapturing || isSubmitting}
                  >
                    {isCapturing ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-1" />
                        Capturing...
                      </>
                    ) : (
                      <>
                        <i className="fa-light fa-camera me-1" />
                        Capture Screenshot
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isSubmitting}
                  >
                    <i className="fa-light fa-upload me-1" />
                    Upload Image
                  </Button>
                  {screenshot && (
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => setScreenshot(null)}
                      disabled={isSubmitting}
                    >
                      <i className="fa-light fa-trash me-1" />
                      Remove
                    </Button>
                  )}
                </div>
                <input
                  type="file"
                  accept="image/*"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  onChange={handleFileUpload}
                />
                {screenshot && (
                  <div className="border rounded p-2">
                    <ImageAnnotator
                      ref={annotatorRef}
                      imageUrl={screenshot}
                      onUpdateImage={setScreenshot}
                      maxWidth={680}
                      maxHeight={380}
                    />
                  </div>
                )}
              </Form.Group>
            </Form>
          </Modal.Body>

          <Modal.Footer className="justify-content-between">
            <Button
              variant="secondary"
              className="d-flex align-items-center justify-content-center"
              onClick={() => setView("chat")}
              disabled={isSubmitting}
              style={{ minWidth: "120px" }}
            >
              <i className="fa-regular fa-angle-left ms-0 me-2"></i> Back
            </Button>
            <div className="d-flex gap-1 text-center">
            <Button
              variant="secondary"
              onClick={handleClose}
              disabled={isSubmitting}
              style={{ minWidth: "120px" }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={isSubmitting || !description.trim()}
              style={{ minWidth: "120px" }}
            >
              {isSubmitting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-1" />
                  Submitting...
                </>
              ) : (
                <>
                  <i className="fa-light fa-paper-plane me-2" />
                  Send Request
                </>
              )}
            </Button>
            </div>
          </Modal.Footer>
        </>
      )}
    </Modal>
  );
}
